import {
  Authority,
  DataSourceStatus,
  DataSourceType,
  IDataSource,
  IDataSourceApp,
  IDataSourceFilter,
  IPaginationFilters,
  QUERIES,
  dataSourcesInfo,
  getRelatedCacheKey,
  isEmpty,
  useAppLayout,
  useDebounce,
  useMountedEffect,
} from "@dataone/lib";
import { Button, Empty, Input, Typography, notification } from "antd";
import { Suspense, useRef, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { styled } from "styled-components";
import {
  CloudIcon,
  DataSetIcon,
  EntrepriseConnectorIcon,
  FileIcon,
  NotificationIcon,
  PlusIcon,
  SearchIcon,
} from "@dataone/icons";
import { Loading } from "../Loading";
import { useAuth } from "../../../../auth/contexts/auth.provider";
import { useNavigate } from "react-router-dom";
import DataSourceModal from "../../../../data-center/components/Modal/DataSourceModal";
import DataSourceStepsForm from "../../../../data-center/components/steps/DataSourceStepsForm";
import { useQueryClient } from "@tanstack/react-query";

interface ChatDatasourcesListProps {
  selectedDatasources: dataSourcesInfo[];
  isOpen?: boolean;
  IsLoading?: boolean;
  datasources?: IDataSource[];
  datasetsFilters?: IPaginationFilters<IDataSourceFilter, IDataSource>;
  setDatasetsFilters?: React.Dispatch<
    React.SetStateAction<IPaginationFilters<IDataSourceFilter, IDataSource>>
  >;
  setSelectedDatasources: React.Dispatch<
    React.SetStateAction<dataSourcesInfo[]>
  >;
}

const ChatDatasourcesList = ({
  IsLoading,
  datasources,
  datasetsFilters,
  selectedDatasources,
  isOpen,
  setDatasetsFilters,
  setSelectedDatasources,
}: ChatDatasourcesListProps) => {
  const [isSearchLoading, setSearchLoading] = useState(false);
  const [searchDatasource, setSearchDatasource] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [open, setOpen] = useState(false);
  const [formMode, setFormMode] = useState<"update" | "create">("create");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [current, setCurrent] = useState(0);
  const [iLoading, setILoading] = useState(false);
  const [dataSource, setDataSource] = useState<IDataSourceApp | null>(null);
  const debouncedSearch = useDebounce(searchDatasource);
  const { user } = useAuth();
  const navigate = useNavigate();
  const checkPlansBtn = (
    <StyledNotifBtn
      onClick={() => {
        navigate("/subscription");
      }}
    >
      Check Plans
    </StyledNotifBtn>
  );
  const checkSubscriptionBtn = (
    <StyledNotifBtn
      onClick={() => {
        useAppLayout.setState({ isSmartSearchVisible: false });
        useAppLayout.setState({ isResponseVisible: false });
        navigate("/profile/subscription-settings");
      }}
    >
      Check Subscription
    </StyledNotifBtn>
  );

  const [api, contextHolder] = notification.useNotification({
    stack: {
      threshold: 1,
    },
  });

  const handleFilterChange = (value: string) => {
    setSearchLoading(true);
    setSearchDatasource(value);
    if (value === debouncedSearch) setSearchLoading(false);
  };

  useMountedEffect(() => {
    setDatasetsFilters &&
      setDatasetsFilters((prev) => ({
        ...prev,
        body: { displayName: debouncedSearch! },
        pageNo: 0,
      }));
    setSearchLoading(false);
  }, [debouncedSearch]);

  const openNotification = () => {
    user?.groups[0] === Authority.OG_SUBSCRIBER
      ? api.open({
          message: "Info",
          description:
            "You've reached the maximum allowed for your current package. Please upgrade your package to add more data sources.",
          btn: checkPlansBtn,
          icon: (
            <span style={{ color: "#1677FF" }}>
              <NotificationIcon />
            </span>
          ),
          duration: null,
        })
      : api.open({
          message: "Info",
          description:
            "Data source is deactivated, please check your subscription status",
          btn: checkSubscriptionBtn,
          icon: (
            <span style={{ color: "#1677FF" }}>
              <NotificationIcon />
            </span>
          ),
          duration: null,
        });
  };

  const handleSelectDatasource = (index: number) => {
    const selectedDatasource = {
      id: (datasources && datasources[index].id) || "",
      type:
        (datasources && datasources[index].dataSourceType) ||
        DataSourceType.DATABASE,
      name: (datasources && datasources[index].displayName) || "",
    };
    if (selectedDatasource) {
      if (
        selectedDatasources.some(
          (ds) =>
            ds.id === selectedDatasource.id &&
            ds.type === selectedDatasource.type
        )
      ) {
        setSelectedDatasources((prev) =>
          prev.filter((ds) => ds.id !== selectedDatasource.id)
        );
      } else {
        if (
          (user?.groups[0] === Authority.OG_SUBSCRIBER &&
            selectedDatasources.length <
              user?.subscription?.multipleDatasourceChatLimit) ||
          user?.groups[0] === Authority.OG_ADMIN
        ) {
          setSelectedDatasources((prev) => [
            ...prev,
            {
              id: selectedDatasource.id,
              type: selectedDatasource.type,
              name: selectedDatasource.name,
            },
          ]);
        } else {
          openNotification();
        }
      }
    }
  };

  const showModal = () => {
    setOpen(true);
    setFormMode("create");
  };

  const handleOk = () => {
    setConfirmLoading(true);
    setTimeout(() => {
      setOpen(false);
      setConfirmLoading(false);
    }, 2000);
  };

  const handleCancel = () => {
    setCurrent(0);
    setOpen(false);
    setFormMode("create");
    setFailed(false);
  };

  const handleGoBack = () => {
    if (!failed) {
      setCurrent(current - 1);
    }
    setFailed(false);
  };

  const queryClient = useQueryClient();
  const handleActionSucceededUpdates = () => {
    const keys = getRelatedCacheKey(queryClient, [QUERIES.DATA_SETS]);
    keys.forEach((key) => {
      queryClient.removeQueries(key);
    });
    setDatasetsFilters!((prev) => ({
      ...prev,
      beforeDate: new Date(),
    }));
  };
  const modalRef = useRef<HTMLDivElement>(null);

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Prevent clicks inside the modal from propagating to the parent elements
    event.stopPropagation();
  };
  return (
    <StyledChatDatasourcesList $isOpen={isOpen}>
      {contextHolder}
      <StyledFirstContainer>
        <StyledSearchInput>
          <Input
            type="text"
            onChange={(e) => handleFilterChange(e.target.value)}
            suffix={
              isSearchLoading ? (
                <LoadingOutlined />
              ) : (
                <StyledSearchIcon>
                  <SearchIcon />
                </StyledSearchIcon>
              )
            }
            maxLength={255}
          ></Input>
        </StyledSearchInput>
      </StyledFirstContainer>
      <StyledSecondContainer>
        {IsLoading ? (
          <Loading />
        ) : isEmpty(datasources) ? (
          <StyledEmptyContainer>
            <Empty />
          </StyledEmptyContainer>
        ) : (
          datasources?.map((datasource, index) => (
            <StyledDatasourceDetails
              key={index}
              onClick={() => handleSelectDatasource(index)}
              $deactivated={datasource.status === DataSourceStatus.DEACTIVATE}
              $selected={selectedDatasources.some(
                (ds) =>
                  ds.id === datasource.id &&
                  ds.type === datasource.dataSourceType
              )}
            >
              <StyledIcons>
                {datasource.dataSourceType ===
                DataSourceType.ENTERPRISE_CONNECTOR ? (
                  <EntrepriseConnectorIcon />
                ) : datasource.internalType === "DRIVE" ? (
                  <CloudIcon />
                ) : datasource.dataSourceType === DataSourceType.FILE ? (
                  <FileIcon />
                ) : (
                  <DataSetIcon />
                )}
              </StyledIcons>
              <StyledDatasourceTitle>
                <Typography.Paragraph
                  editable={false}
                  style={{
                    margin: "0px",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                  ellipsis={{
                    tooltip: `${datasource.displayName}`,
                  }}
                >
                  {datasource.displayName}
                </Typography.Paragraph>
                {/* <span className="datasource-title">
                  {datasource.displayName}
                </span> */}
                <span className="datasource-type">
                  {datasource.internalType === "DRIVE"
                    ? "GOOGLE DRIVE"
                    : datasource.internalType}
                </span>
              </StyledDatasourceTitle>
            </StyledDatasourceDetails>
          ))
        )}
      </StyledSecondContainer>
      <StyledAddButton onClick={showModal}>
        <PlusIcon />
        <span className="datasource-title">Add New</span>
      </StyledAddButton>
      <Suspense fallback={<></>}>
        <div ref={modalRef} onClick={handleModalClick}>
          <DataSourceModal
            modalTitle="Create a new data source"
            containerId="#smartsearch-modal"
            open={open}
            confirmLoading={confirmLoading}
            handleOk={handleOk}
            handleCancel={handleCancel}
            handleGoBack={handleGoBack}
            isDeleteModal={false}
            failed={failed}
            children={
              <DataSourceStepsForm
                dataSourceData={null}
                setCurrent={setCurrent}
                current={current}
                iLoading={iLoading}
                setILoading={setILoading}
                failed={failed}
                dataSource={dataSource}
                setDataSource={setDataSource}
                setFailed={setFailed}
                formMode={formMode}
                onCreateSuccess={handleActionSucceededUpdates}
              />
            }
            current={current}
          />
        </div>
      </Suspense>
    </StyledChatDatasourcesList>
  );
};

export default ChatDatasourcesList;

const StyledChatDatasourcesList = styled.div<{ $isOpen?: boolean }>`
  position: absolute;
  z-index: 100;
  transition: width 3s ease-in-out;
  ${(p) =>
    p.$isOpen
      ? ` 
  bottom: 82px;
  width: 300px;
  background: #ffffff;
  padding: 10px;
  border-radius: 13px;
  transition: width 3s ease-in-out;
  `
      : ` width: 0px; transition: width 3s ease-in-out;`}
`;
const StyledFirstContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const StyledSearchInput = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: center;
  .ant-input-affix-wrapper {
    border: none;
    border-radius: 30px;
    padding: 10px 25px;
    background: #eff1fd;
    height: 37px;
    width: 255px;
    ::placeholder {
      color: "#4974F6";
      font-size: 20px;
      font-weight: 400;
    }
    .ant-input {
      color: rgba(4, 7, 38, 0.75);
      font-size: 13px;
      font-weight: 400;
      letter-spacing: -0.3px;
      background: #eff1fd;
    }
  }
`;
const StyledSearchIcon = styled.div`
  font-size: 16px;
  display: flex;
  color: #4974f6;
`;
const StyledEmptyContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
  .ant-empty {
    font-size: 12px;
    .ant-empty-image {
      height: 80px;
    }
  }
`;
const StyledDatasourceDetails = styled.div<{
  $selected?: boolean;
  $deactivated?: boolean;
}>`
  display: flex;
  flex-direction: row;
  position: relative;
  border-radius: 30px;
  cursor: pointer;
  height: 46px;
  margin: 10px 0;
  img {
    position: absolute;
    height: 42px;
  }
  &:hover {
    box-shadow: 3px -3px 3px -1px rgba(73, 116, 246, 0.25);
  }
  ${(props) =>
    props.$selected &&
    `background: #EFF1FD; box-shadow: 3px -3px 3px -1px rgba(73, 116, 246, 0.25);`}

  ${({ $deactivated }) =>
    $deactivated &&
    `cursor: not-allowed; opacity: 0.6; &:hover {box-shadow: none;}`}
`;
const StyledIcons = styled.div`
  font-size: 20px;
  cursor: pointer;
  font-size: 17px;
  color: #4974f6;
  padding: 13px;
  background: #eff1fd;
  border-radius: 50%;
  display: flex;
  position: absolute;
`;
const StyledDatasourceTitle = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 0 58px;
  background: transparent;
  border-radius: 32px;
  justify-content: center;
  height: 39px;
  width: 190px;
  .datasource-title {
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 96%;
    text-wrap: nowrap;
  }
`;
const StyledNotifBtn = styled(Button)`
  background-color: #4974f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  height: 32px;
  font-size: 13px;
  &:hover {
    color: #fff !important;
    border: none;
    opacity: 0.8;
  }
`;
const StyledSecondContainer = styled.div`
  height: 26vh;
  margin-right: 14px;
  padding: 13px;
  overflow-y: auto;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #dbe4fd;
    border-radius: 7px;
  }

  &::-webkit-scrollbar-button {
    display: block;
    height: 16px;
    width: 16px;
    background-color: transparent;
    border: none;
  }
`;
const StyledAddButton = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  svg {
    color: #4974f6;
    font-size: 20px;
  }
  span {
    font-size: 13px;
    font-weight: 700;
    color: #4974f6;
    margin-left: 5px;
  }
`;
